import React, { useMemo, useState } from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { Add } from '@mui/icons-material';
import { Form2 } from '../../../toolympus/components/primitives/Forms2';
import { LoadingIndicator, OccupyFreeSpace, PseudoLink2, SearchField } from '../../../toolympus/components/primitives';
import { Link } from '../../../toolympus/components/primitives/Common.styles';
import { FormControlsForFields, MaybeFieldDefinition, TableForFields } from '../../../toolympus/components/schemed';
import { DateDisplayWithDatetimeTooltip } from '../../../toolympus/components/schemed/TimeDisplay';
import { FieldType, Schema } from '../../../toolympus/hooks/useSchema';
import { useLoadedData } from '../../../toolympus/hooks/useLoadedData';
import { CalculationRecord, useCalculationsList } from './useCalculationsList';
import { CalculatorConfigRecord } from '../../../toolympus/components/Calculator/storage/useCalculatorConfigsList';
import { CalculatorConfig } from '../../../toolympus/components/Calculator/types';
import { getDefaultCalculatorValues } from '../../../toolympus/components/Calculator/useCalculatorRunnerState';
import { runPipeline } from '../../../toolympus/components/Calculator/engine/pipeline';
import { CalculationEditDrawer } from './CalculationEditDrawer';

const filterSchema: Schema = {
  calculator_id: {
    type: FieldType.dictionarySelect,
    label: 'Калькулятор',
    dictionary: 'Calculators',
  },
};

const calculationsBaseSchema: Schema = {
  title: {
    type: FieldType.text,
    label: 'Название',
  },
  comment: {
    type: FieldType.text,
    label: 'Комментарий',
  },
  created_by: {
    type: FieldType.dictionarySelect,
    dictionary: 'Users',
    label: 'Создатель',
  },
  created_at: {
    type: FieldType.datetime,
    label: 'Создано',
  },
};

export function filterInjectedValues(
  config: CalculatorConfig | CalculatorConfigRecord,
  sourceValues: Record<string, any> = {}
): Record<string, any> {
  const result: Record<string, any> = {};
  const inputs = config.inputs || [];

  for (const input of inputs) {
    if (!input || !input.name) continue;
    if (!(input.name in sourceValues) || sourceValues[input.name] === undefined) {
      continue;
    }

    const val = sourceValues[input.name];

    if (input.type === 'select') {
      const options = input.options || [];
      const hasMatchingOption = options.some(
        (opt) => opt.value === val || String(opt.value) === String(val)
      );
      if (!hasMatchingOption) {
        continue;
      }
    }

    result[input.name] = val;
  }

  return result;
}

interface FilterParams {
  calculator_id?: string;
}

export interface CalculationsListPageProps {
  apiPath?: string;
  configApiPath?: string;
}

export const CalculationsListPage: React.FC<CalculationsListPageProps> = ({
  apiPath = '/api/calculations',
  configApiPath = '/api/calculations/config',
}) => {
  const [params, setParams] = useState<FilterParams>({});
  const selectedCalculatorId = params.calculator_id;

  const calculationsList = useCalculationsList(selectedCalculatorId, apiPath);

  const calculatorConfig = useLoadedData<CalculatorConfigRecord | null>(
    selectedCalculatorId ? `${configApiPath}/${selectedCalculatorId}` : '',
    null,
    !!selectedCalculatorId
  );

  const resultOutputs = useMemo(() => {
    if (!calculatorConfig.data?.outputs) {
      return [];
    }
    return calculatorConfig.data.outputs.filter((o) => o.isResult && !o.is_hidden);
  }, [calculatorConfig.data]);

  const resultFieldKeys = useMemo(() => {
    return resultOutputs.map((o) => o.field);
  }, [resultOutputs]);

  const fields: MaybeFieldDefinition[] = useMemo(() => {
    const list: MaybeFieldDefinition[] = [
      ['title', { label: 'Название' }],
      ['comment', { label: 'Комментарий' }],
    ];

    resultOutputs.forEach((output) => {
      list.push([output.field, { label: output.name || output.field }]);
    });

    list.push(['created_by', { label: 'Создатель' }]);
    list.push(['created_at', { label: 'Создано' }]);

    return list;
  }, [resultOutputs]);

  const schema = useMemo(() => {
    const s: Schema = { ...calculationsBaseSchema };
    resultOutputs.forEach((output) => {
      s[output.field] = {
        type: FieldType.text,
        label: output.name || output.field,
      };
    });
    return s;
  }, [resultOutputs]);

  return (
    <Form2
      noTitle
      background="panel"
      fitFullHeight
      headerItems={
        <Box sx={{ display: 'flex', alignItems: 'flex-end', width: '100%', gap: 1 }}>
          <Box sx={{ minWidth: 260 }}>
            <FormControlsForFields
              schema={filterSchema}
              data={params}
              fields={[['calculator_id']]}
              onChange={(_, changes) => {
                setParams((prev) => ({ ...prev, ...changes }));
              }}
            />
          </Box>
          {!!selectedCalculatorId && (
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                const initialVals = calculatorConfig.data
                  ? getDefaultCalculatorValues(calculatorConfig.data)
                  : {};
                calculationsList.newRecord.startEditing({
                  reference_id: selectedCalculatorId,
                  title: calculatorConfig.data?.title || '',
                  values: initialVals,
                });
              }}
            >
              <Add fontSize="small" />
            </IconButton>
          )}

          <OccupyFreeSpace />

          {calculationsList.isLoading && (
            <Box sx={{ mb: 0.75, display: 'flex', alignItems: 'center' }}>
              <LoadingIndicator sizeVariant="s" />
            </Box>
          )}

          <SearchField
            filter={calculationsList.filter.filter}
            setFilter={calculationsList.filter.setFilter}
            noButton
          />

          <Link to="/calculators" style={{ alignSelf: 'flex-end' }}>
            <Button color="primary">
              калькуляторы
            </Button>
          </Link>
        </Box>
      }
    >
      <TableForFields<CalculationRecord>
        data={calculationsList.data || []}
        fields={fields}
        schema={schema}
        fieldElement={(f) => {
          if (f === 'title') {
            return (row: CalculationRecord) => (
              <PseudoLink2 onClick={() => calculationsList.editRecord.startEditing(row)}>
                {row.title}
              </PseudoLink2>
            );
          }
          if (f === 'created_at') {
            return (row: CalculationRecord) => (
              <DateDisplayWithDatetimeTooltip
                field="created_at"
                row={row}
                schema={schema.created_at}
              />
            );
          }
          if (resultFieldKeys.includes(f)) {
            return (row: CalculationRecord) => {
              const val = row.values?.[f];
              if (val === undefined || val === null) {
                return '';
              }
              if (typeof val === 'object') {
                return JSON.stringify(val);
              }
              return String(val);
            };
          }
          return undefined;
        }}
      />
      <CalculationEditDrawer
        config={calculatorConfig.data}
        data={calculationsList.newRecord}
      />
      <CalculationEditDrawer
        config={calculatorConfig.data}
        data={calculationsList.editRecord}
        onCreateBased={(sourceItem) => {
          const actualConfig = calculatorConfig.data;
          if (!actualConfig || actualConfig._id !== sourceItem.reference_id) {
            return;
          }

          const normalizedConfig: CalculatorConfig = {
            _id: actualConfig._id || sourceItem.reference_id || '',
            title: actualConfig.title,
            description: actualConfig.description,
            description_more: actualConfig.description_more,
            inputs: actualConfig.inputs || [],
            rules: actualConfig.rules || [],
            outputs: actualConfig.outputs || [],
          };

          const injected = filterInjectedValues(normalizedConfig, sourceItem.values || {});
          let initialVals = getDefaultCalculatorValues(normalizedConfig, injected);
          try {
            const result = runPipeline(normalizedConfig, initialVals);
            initialVals = { ...initialVals, ...result.values };
          } catch (e) {
            // ignore
          }

          calculationsList.editRecord.cancel();
          calculationsList.newRecord.startEditing({
            reference_id: sourceItem.reference_id,
            title: sourceItem.title || '',
            comment: sourceItem.comment || '',
            values: initialVals,
          });
        }}
      />
    </Form2>
  );
};
